// src/components/legal/CubiePopAccountDeletion.js
import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const Container = styled.div`
    max-width: 800px;
    margin: 0 auto;
    padding: 3rem 2rem;
    line-height: 1.8;
    color: #333;
`;

const Header = styled.header`
    text-align: center;
    margin-bottom: 3rem;
    padding-bottom: 2rem;
    border-bottom: 2px solid #eee;
`;

const Title = styled.h1`
    font-size: 2.5rem;
    margin-bottom: 1rem;
    color: #7C4DFF;
`;

const UpdateDate = styled.p`
    color: #666;
    font-size: 1rem;
`;

const BackLink = styled(Link)`
    display: inline-flex;
    align-items: center;
    color: #7C4DFF;
    text-decoration: none;
    margin-bottom: 2rem;
    transition: opacity 0.3s;

    &:hover {
        opacity: 0.8;
    }

    &::before {
        content: '←';
        margin-right: 0.5rem;
    }
`;

const Section = styled.section`
    margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: #444;
`;

const Paragraph = styled.p`
    margin-bottom: 1rem;
    text-align: justify;
    white-space: pre-wrap;
`;

const WarningBox = styled.div`
    background: #FFEBEE;
    border-left: 4px solid #F44336;
    padding: 1.5rem;
    border-radius: 5px;
    margin: 2rem 0;
`;

const InfoBox = styled.div`
    background: #F1ECFF;
    padding: 1.5rem;
    border-radius: 10px;
    margin: 2rem 0;
    white-space: pre-wrap;
`;

const StepBox = styled.div`
    background: #F5F5F5;
    padding: 1.5rem;
    border-radius: 10px;
    margin: 1rem 0;

    h3 {
        color: #7C4DFF;
        margin-bottom: 0.5rem;
    }
`;

const ContactBox = styled.div`
    background: #EDE7FF;
    padding: 1.5rem;
    border-radius: 10px;
    margin: 2rem 0;
`;

const CubiePopAccountDeletion = () => {
    return (
        <Container>
            <BackLink to="/">홈으로 돌아가기 / Back to Home</BackLink>

            <Header>
                <Title>CubiePop Data Deletion / 데이터 삭제 안내</Title>
                <UpdateDate>Last updated / 최종 수정일: June 1, 2026</UpdateDate>
            </Header>

            <WarningBox>
                <strong>Notice / 안내:</strong> CubiePop does not require account registration to play. Game progress is stored locally on your device. Some optional features (cloud save, leaderboards, Google sign-in) use Firebase Authentication and Cloud Firestore — when you use these, additional data (a Firebase UID, a cloud-save document, and leaderboard entries) is stored on our servers and can be deleted on request.
                <br /><br />
                CubiePop는 플레이 자체엔 회원가입이 필요 없으며, 게임 진행은 기기 로컬에 저장됩니다. 선택 기능(클라우드 세이브·랭킹·Google 로그인) 사용 시 Firebase Authentication·Cloud Firestore에 추가 데이터(Firebase UID, 클라우드 세이브 문서, 랭킹 엔트리)가 저장되며, 요청 시 삭제됩니다.
            </WarningBox>

            <Section>
                <SectionTitle>How to Delete Your Data / 데이터 삭제 방법</SectionTitle>

                <StepBox>
                    <h3>Method 1: Uninstall the app / 앱 삭제</h3>
                    <Paragraph>
                        Uninstalling CubiePop from your device removes all locally stored game progress, coins, lives, settings, and cached data.

                        Steps:
                        1. Long-press the CubiePop icon on your device
                        2. Tap "Uninstall" / "App info → Uninstall"
                        3. Confirm

                        앱을 삭제하면 기기에 저장된 모든 게임 진행 정보·코인·라이프·설정·캐시 데이터가 함께 삭제됩니다.
                    </Paragraph>
                </StepBox>

                <StepBox>
                    <h3>Method 2: Reset Advertising ID / 광고 ID 초기화</h3>
                    <Paragraph>
                        To break the link between you and advertising data already collected, reset your device Advertising ID:

                        Android Steps:
                        1. Open device Settings → Google → Ads
                        2. Tap "Delete advertising ID" (or "Reset advertising ID")
                        3. Confirm

                        iOS Steps:
                        1. Settings → Privacy & Security → Tracking / Apple Advertising
                        2. Disable tracking / reset Apple Advertising Identifier

                        After reset, any future data is associated with a new identifier and is no longer linkable to your old data.

                        광고 ID 초기화: 기기 설정 → Google → 광고 → "광고 ID 삭제"(Android) 또는 iOS의 추적/광고 설정에서 초기화하면 향후 수집되는 데이터는 새로운 식별자와 연결됩니다.
                    </Paragraph>
                </StepBox>

                <StepBox>
                    <h3>Method 3: Sign out of Google in-game / 게임 내 Google 로그아웃</h3>
                    <Paragraph>
                        If you have signed in with Google for cloud save or leaderboards, you can unlink the Google credential from in-game Settings → Account → "Sign out". This stops further cloud syncing from this device.

                        Note: Sign-out alone does NOT delete the server-side cloud save document, leaderboard entries, or the Firebase Authentication account. To erase those records, use Method 4 below (email request). A self-service "Delete cloud data" button may be added to in-game Settings in a future update; until then, email is the primary path for full server-side deletion.

                        로그아웃만으로는 서버의 클라우드 세이브 문서·랭킹 엔트리·Firebase Authentication 계정이 삭제되지 않습니다. 서버 데이터 완전 삭제는 아래 Method 4(이메일 요청)로 진행해 주세요. 향후 업데이트에 게임 내 "클라우드 데이터 삭제" 버튼이 추가될 수 있습니다.
                    </Paragraph>
                </StepBox>

                <StepBox>
                    <h3>Method 4: Email Request / 이메일 요청 (GDPR Article 17, PIPA Art. 36)</h3>
                    <Paragraph>
                        To request deletion of data we hold (advertising identifier records, ad/analytics events, purchase records, support correspondence, cloud save, leaderboard entries, Firebase Authentication account), email us with the following:

                        Send to: mind-wellness@naver.com
                        Subject: [Data Deletion Request] CubiePop

                        Include any of the identifiers below that you have available:
                        • Your Advertising ID (Android: Settings → Google → Ads → Your advertising ID)
                        • If you signed in with Google: the Google account email associated with your Google Play Games profile (used to locate your Firebase UID)
                        • The Google Play Games display name and approximate country shown on your leaderboard entry (if you used leaderboards)
                        • Approximate first install date and country
                        • Email used for any in-app purchase receipt (if applicable)
                        • Order ID from the platform store (if you purchased)
                        • Reason for deletion (optional)

                        Processing time: We will confirm receipt within 5 business days and complete deletion within 30 days, in line with GDPR Article 12.

                        삭제 요청: 위 정보를 포함하여 mind-wellness@naver.com으로 이메일을 보내주세요. 30일 이내 처리됩니다.
                    </Paragraph>
                </StepBox>
            </Section>

            <Section>
                <SectionTitle>Data That Will Be Deleted / 삭제되는 데이터</SectionTitle>
                <Paragraph>
                    <strong>Server-side (on request):</strong>
                    • Advertising identifier records linked to your Advertising ID
                    • Ad/analytics events (level reached, sessions, ad impressions) tied to your Advertising ID
                    • Firebase Authentication account (the anonymous UID, and any Google credential you linked)
                    • Cloud save document at Firestore path users/[your-uid] (snapshot of saved game state: level, coins, lives, stars, items, settings)
                    • Leaderboard entries (score / level / display name / country code)
                    • Customer support emails and any provided identifiers
                    • Consent records (after the legal retention period)

                    <strong>Locally stored (on uninstall):</strong>
                    • Game progress, coin balance, lives, settings (PlayerPrefs)
                    • Cached assets

                    <strong>Third-party processors:</strong>
                    • Unity Ads: deletion is propagated to Unity's systems, subject to Unity's processing timelines
                    • Firebase Authentication & Cloud Firestore: documents and the account are deleted immediately; Google may retain operational backups for a limited period (typically up to 180 days) before final purge
                    • Platform store billing: managed via your Google / Apple account
                    • Google Play Games Services (sign-in): unlinking removes the credential association on our side; your Google Play Games profile itself is unaffected and is managed in your Google account
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>Data That Must Be Retained / 보존되는 데이터</SectionTitle>
                <Paragraph>
                    We are legally required to retain certain records:

                    <strong>Korean Electronic Commerce Act / 전자상거래법:</strong>
                    • Contract & withdrawal records: 5 years
                    • Payment & supply records: 5 years
                    • Consumer complaints or dispute records: 3 years

                    <strong>Korean Communications Privacy Act / 통신비밀보호법:</strong>
                    • Access logs (login/connection records): 3 months

                    <strong>EU/Korean tax law / 세법:</strong>
                    • VAT and tax-related records: as required by applicable tax authority

                    <strong>GDPR accountability / GDPR 책임성 원칙:</strong>
                    • Records of consent and processing activities (Art. 30): up to 3 years after withdrawal

                    These records are stored in pseudonymized or aggregated form where possible and are not used for ongoing processing once retention is no longer justified.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>Data Portability / 데이터 이동권</SectionTitle>
                <Paragraph>
                    Under GDPR Article 20, you have the right to receive your personal data in a structured, commonly used, and machine-readable format.

                    To request a data export:
                    • Email mind-wellness@naver.com with subject "[Data Export Request] CubiePop"
                    • Include your Advertising ID
                    • Format: JSON (default). CSV available on request.
                    • Delivery: within 30 days, free of charge for the first request per year

                    Note: Most game progress is stored locally on your device and is already in your possession.

                    데이터 내보내기: 위 정보를 포함하여 이메일로 요청 시 30일 이내 JSON 형식으로 제공합니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>Identity Verification / 본인 확인</SectionTitle>
                <Paragraph>
                    Because CubiePop does not require account registration, we use the following identifiers to locate your data:

                    • Advertising ID (AAID / IDFA) — for advertising records. If the Advertising ID has been reset, we may not be able to locate prior records — in that case, deletion may have already effectively occurred from your perspective.
                    • Firebase UID — for cloud save and leaderboard records. If you linked Google, we can also locate the UID from your Google account identifier.
                    • Platform store Order ID — for purchase-related deletion.

                    We will not request government-issued ID. If we cannot verify your request based on the information provided, we will inform you and explain why.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>Effect of Deletion / 삭제 후 영향</SectionTitle>
                <Paragraph>
                    • Your gameplay/ad analytics will no longer be linked to you.
                    • Personalized advertising profiles tied to your Advertising ID will be reset.
                    • Your cloud save document is permanently deleted. The next launch will fall back to the local save on your current device. If you also uninstalled or wiped local data, you start as a new user.
                    • Your leaderboard entries are removed. You may re-enter leaderboards later with a fresh account, but no historical record will be restored.
                    • Your Firebase Authentication account is deleted; the next launch creates a new anonymous UID. If you re-link Google, the link to your Google account is fresh.
                    • In-app purchases of non-consumable items may need to be restored via the platform store's Restore Purchases.

                    삭제 후: 분석 데이터 연결이 해제되고 광고 프로필이 초기화됩니다. 클라우드 세이브·랭킹 데이터·Firebase Authentication 계정은 영구 삭제되며, 다음 실행 시 새 익명 UID가 발급됩니다. 영구 구매 항목은 플랫폼 스토어 구매 복원 기능으로 복원 가능합니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>Children's Data / 아동 데이터</SectionTitle>
                <Paragraph>
                    CubiePop is rated 13+ (16+ in some EEA jurisdictions). If you believe a child below the applicable minimum age has used the game, contact mind-wellness@naver.com with the Advertising ID and approximate use period. We will treat such requests as a priority and delete data without requiring identity verification typically required for adult requests, in accordance with COPPA, GDPR Article 8, and Korean PIPA Article 22-2.

                    아동 데이터: 만 13세 미만(EU 일부 16세 미만) 사용 의심 시 즉시 처리되며, 일반 본인확인 절차를 면제합니다.
                </Paragraph>
            </Section>

            <ContactBox>
                <SectionTitle>Customer Support / 고객 지원</SectionTitle>
                <Paragraph>
                    Need help with data deletion or have privacy concerns?

                    • Email: mind-wellness@naver.com
                    • Response time: within 48 business hours
                    • Business hours: Mon–Fri 09:00–18:00 KST

                    FAQ:
                    • Q: Can I recover deleted data? → No. Deletion is permanent.
                    • Q: Can I reinstall after deletion? → Yes. You will start as a new user.
                    • Q: How do I cancel a subscription? → Manage via the platform store → Subscriptions. Deletion of our records does not auto-cancel subscriptions.
                    • Q: Will I be charged for the deletion request? → No. First request per 12-month period is free.
                </Paragraph>
            </ContactBox>

            <InfoBox>
                <SectionTitle>Your Legal Rights / 법적 권리</SectionTitle>
                <Paragraph>
                    <strong>GDPR (EEA/UK/Switzerland):</strong>
                    • Right to erasure (Art. 17)
                    • Right to data portability (Art. 20)
                    • Right to object to processing (Art. 21)
                    • Right to lodge a complaint with a supervisory authority

                    <strong>CCPA/CPRA (California):</strong>
                    • Right to delete
                    • Right to know
                    • Right to opt out of "sale" or "sharing"
                    • Right to non-discrimination

                    <strong>PIPA (Korea / 한국):</strong>
                    • 개인정보 삭제 요구권 (제36조)
                    • 처리정지 요구권 (제37조)
                    • 동의 철회권

                    Exercise rights: mind-wellness@naver.com
                </Paragraph>
            </InfoBox>

            <InfoBox>
                <SectionTitle>Document Information / 문서 정보</SectionTitle>
                <Paragraph>
                    • Originally published / 최초 작성일: 2026-06-01
                    • Last updated / 최종 수정일: 2026-06-01
                    • Version / 버전: 1.0
                    • Next review / 다음 검토일: 2026-12-01
                </Paragraph>
            </InfoBox>
        </Container>
    );
};

export default CubiePopAccountDeletion;
